import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Stack,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { ArrowBack, Edit, Assessment } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { examinationAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';

const ExaminationView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();
  const [examination, setExamination] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchExamination();
  }, [id]);

  const fetchExamination = async () => {
    try {
      setLoading(true);
      const response = await examinationAPI.getOne(id);
      setExamination(response.data.data?.examination || response.data.examination || response.data);
    } catch (error) {
      console.error('Error fetching examination:', error);
      toast.error('Failed to load examination details');
      navigate('/admin/examinations');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'upcoming': return 'info';
      case 'ongoing': return 'success';
      case 'completed': return 'default';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const calculateTotals = () => {
    if (!examination?.subjects) return { totalMarks: 0, passingMarks: 0, duration: 0 };
    return {
      totalMarks: examination.subjects.reduce((sum, s) => sum + (s.totalMarks || 0), 0),
      passingMarks: examination.subjects.reduce((sum, s) => sum + (s.passingMarks || 0), 0),
      duration: examination.subjects.reduce((sum, s) => sum + (s.duration || 0), 0),
    };
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!examination) {
    return (
      <Box>
        <Typography variant="h6">Examination not found</Typography>
        <Button onClick={() => navigate('/admin/examinations')} sx={{ mt: 2 }}>
          Back to Examinations
        </Button>
      </Box>
    );
  }

  const totals = calculateTotals();

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/admin/examinations')}
          >
            Back
          </Button>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {examination.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Code: {examination.code}
            </Typography>
          </Box>
        </Box>
        {isSuperAdmin && (
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<Assessment />}
              onClick={() => navigate(`/admin/examinations/${id}/stats`)}
            >
              View Stats
            </Button>
            <Button
              variant="contained"
              startIcon={<Edit />}
              onClick={() => navigate(`/admin/examinations/edit/${id}`)}
            >
              Edit
            </Button>
          </Stack>
        )}
      </Box>

      <Grid container spacing={3}>
        {/* Basic Info */}
        <Grid item xs={12} md={8}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Examination Details
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Course
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {examination.course?.name || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Semester
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {examination.semester?.semesterName || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Start Date & Time
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {formatDate(examination.startDate)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  End Date & Time
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {formatDate(examination.endDate)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  Total Duration
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {totals.duration} minutes
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  Total Marks
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {totals.totalMarks}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  Passing Marks
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {totals.passingMarks}
                </Typography>
              </Grid>
              {examination.instructions && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Instructions
                  </Typography>
                  <Typography variant="body2">
                    {examination.instructions}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>

        {/* Status Info */}
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Status
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Examination Status
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Chip
                    label={examination.status.toUpperCase()}
                    color={getStatusColor(examination.status)}
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Active Status
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Chip
                    label={examination.isActive ? 'ACTIVE' : 'INACTIVE'}
                    color={examination.isActive ? 'success' : 'default'}
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
              </Box>
            </Stack>
          </Paper>
        </Grid>

        {/* Subjects Table */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Subjects ({examination.subjects?.length || 0})
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Subject Code</strong></TableCell>
                    <TableCell><strong>Subject Name</strong></TableCell>
                    <TableCell align="center"><strong>Total Marks</strong></TableCell>
                    <TableCell align="center"><strong>Passing Marks</strong></TableCell>
                    <TableCell align="center"><strong>Duration (min)</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {examination.subjects && examination.subjects.length > 0 ? (
                    examination.subjects.map((subject, idx) => (
                      <TableRow key={subject._id || idx}>
                        <TableCell>{subject.code}</TableCell>
                        <TableCell>{subject.name}</TableCell>
                        <TableCell align="center">{subject.totalMarks}</TableCell>
                        <TableCell align="center">{subject.passingMarks}</TableCell>
                        <TableCell align="center">{subject.duration}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No subjects added
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ExaminationView;
